import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Lock,
  MessageSquare,
  Paperclip,
  Search,
  Send,
  ShieldAlert,
  Ticket,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import type { SupportTicket, SupportTicketDetail, SupportTicketStatus } from '@terra/api-client';
import { Badge, Card, CardBody, EmptyState, Input, PageHeader, Spinner } from '@terra/ui';

import { api } from '../lib/api';

// ── Customer Support — vendor side (single source of truth) ─────────────────
// The vendor sees only tickets where the platform routed the customer to them
// (via product ownership). Replies, internal notes, attachments and resolution
// are all on the SAME SupportTicket record the admin sees.

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open',
  ASSIGNED: 'Assigned to you',
  IN_PROGRESS: 'In progress',
  WAITING_FOR_CUSTOMER: 'Awaiting customer',
  PENDING_CUSTOMER: 'Awaiting customer',
  ESCALATED: 'Escalated',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

const STATUS_TONE: Record<string, 'warning' | 'danger' | 'success' | 'neutral'> = {
  OPEN: 'warning',
  ASSIGNED: 'warning',
  IN_PROGRESS: 'warning',
  WAITING_FOR_CUSTOMER: 'neutral',
  PENDING_CUSTOMER: 'neutral',
  ESCALATED: 'danger',
  RESOLVED: 'success',
  CLOSED: 'neutral',
};

const PRIORITY_TONE: Record<string, 'warning' | 'danger' | 'neutral'> = {
  LOW: 'neutral',
  NORMAL: 'neutral',
  HIGH: 'warning',
  URGENT: 'danger',
};

export function CustomerSupportPage() {
  const [statusFilter, setStatusFilter] = useState<SupportTicketStatus | undefined>(undefined);
  const [slaFilter, setSlaFilter] = useState<'ALL' | 'BREACHED' | 'AT_RISK'>('ALL');
  const [q, setQ] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);

  const tickets = useQuery({
    queryKey: ['vendor-cust-tickets', statusFilter, slaFilter, q],
    queryFn: () =>
      api.support.vendor.list({
        limit: 100,
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(slaFilter !== 'ALL' ? { slaState: slaFilter } : {}),
        ...(q ? { q } : {}),
      }),
  });

  const counts = useMemo(() => {
    const c = { total: 0, open: 0, awaiting: 0, breached: 0, resolved: 0 };
    for (const t of tickets.data ?? []) {
      c.total += 1;
      if (t.status === 'OPEN' || t.status === 'ASSIGNED' || t.status === 'IN_PROGRESS') c.open += 1;
      if (t.status === 'WAITING_FOR_CUSTOMER' || t.status === 'PENDING_CUSTOMER') c.awaiting += 1;
      if (t.slaBreachedAt) c.breached += 1;
      if (t.status === 'RESOLVED' || t.status === 'CLOSED') c.resolved += 1;
    }
    return c;
  }, [tickets.data]);

  return (
    <div className="space-y-6 pb-10">
      <PageHeader
        title="Customer Support"
        description="Customer tickets routed to your store based on product ownership. Replies and resolutions sync with the admin support console in real time."
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Open" value={counts.open} icon={Ticket} />
        <Stat label="Awaiting customer" value={counts.awaiting} icon={MessageSquare} />
        <Stat label="SLA breached" value={counts.breached} tone="danger" icon={ShieldAlert} />
        <Stat label="Resolved" value={counts.resolved} icon={CheckCircle2} />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_460px]">
        <Card>
          <CardBody className="space-y-4 p-5">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[200px] flex-1">
                <Search className="text-ink-muted absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder="Search by ticket number or subject"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-9"
                />
              </div>
              <select
                value={statusFilter ?? ''}
                onChange={(e) =>
                  setStatusFilter((e.target.value || undefined) as SupportTicketStatus | undefined)
                }
                className="border-border bg-surface h-10 rounded-lg border px-3 text-sm"
              >
                <option value="">All statuses</option>
                {Object.entries(STATUS_LABEL).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              <select
                value={slaFilter}
                onChange={(e) => setSlaFilter(e.target.value as typeof slaFilter)}
                className="border-border bg-surface h-10 rounded-lg border px-3 text-sm"
              >
                <option value="ALL">All SLA</option>
                <option value="AT_RISK">At risk</option>
                <option value="BREACHED">Breached</option>
              </select>
            </div>

            <div>
              {tickets.isLoading ? (
                <Spinner size="lg" />
              ) : (tickets.data ?? []).length === 0 ? (
                <EmptyState
                  icon={<Ticket className="h-10 w-10" />}
                  title="No customer tickets"
                  description="Tickets raised against your products will appear here."
                />
              ) : (
                <ul className="divide-border/60 divide-y">
                  {(tickets.data ?? []).map((t) => (
                    <TicketRow
                      key={t.id}
                      ticket={t}
                      active={activeId === t.id}
                      onOpen={() => setActiveId(t.id)}
                    />
                  ))}
                </ul>
              )}
            </div>
          </CardBody>
        </Card>

        {activeId && <TicketDetail ticketId={activeId} onClose={() => setActiveId(null)} />}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: number;
  icon: LucideIcon;
  tone?: 'danger';
}) {
  return (
    <Card>
      <CardBody className="flex items-center gap-3 p-4">
        <span
          className={`grid h-10 w-10 place-items-center rounded-lg ${
            tone === 'danger' ? 'bg-rose-50 text-rose-600' : 'bg-surface-subtle text-ink-muted'
          }`}
        >
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <p className="text-ink-muted text-xs font-bold uppercase tracking-wide">{label}</p>
          <p className="text-ink text-xl font-bold">{value}</p>
        </div>
      </CardBody>
    </Card>
  );
}

function TicketRow({
  ticket,
  active,
  onOpen,
}: {
  ticket: SupportTicket;
  active: boolean;
  onOpen: () => void;
}) {
  const breached = !!ticket.slaBreachedAt;
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={`hover:bg-surface-subtle/40 flex w-full items-center gap-3 py-3 text-left ${
          active ? 'bg-brand-primary/5' : ''
        }`}
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-ink-muted font-mono text-[11px] font-bold">
              {ticket.ticketNumber}
            </span>
            <Badge tone={STATUS_TONE[ticket.status] ?? 'neutral'}>
              {STATUS_LABEL[ticket.status]}
            </Badge>
            <Badge tone={PRIORITY_TONE[ticket.priority] ?? 'neutral'}>{ticket.priority}</Badge>
            {breached && <Badge tone="danger">SLA breached</Badge>}
          </span>
          <span className="text-ink mt-1 block truncate font-semibold">{ticket.subject}</span>
          <span className="text-ink-muted mt-0.5 block text-[11px]">
            {format(new Date(ticket.updatedAt), 'MMM d, yyyy h:mm a')}
          </span>
        </span>
        <ChevronRight className="text-ink-muted h-4 w-4" />
      </button>
    </li>
  );
}

function TicketDetail({ ticketId, onClose }: { ticketId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);
  const [resolution, setResolution] = useState('');
  const [showResolve, setShowResolve] = useState(false);

  const detail = useQuery<SupportTicketDetail>({
    queryKey: ['vendor-ticket', ticketId],
    queryFn: () => api.support.vendor.get(ticketId),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['vendor-ticket', ticketId] });
    void qc.invalidateQueries({ queryKey: ['vendor-cust-tickets'] });
  };

  const send = useMutation({
    mutationFn: () => api.support.vendor.reply(ticketId, { body: reply, internal }),
    onSuccess: () => {
      setReply('');
      setInternal(false);
      invalidate();
    },
  });

  const resolve = useMutation({
    mutationFn: () => api.support.vendor.resolve(ticketId, { resolutionNote: resolution }),
    onSuccess: () => {
      setResolution('');
      setShowResolve(false);
      invalidate();
    },
  });

  if (detail.isLoading) {
    return (
      <Card>
        <CardBody className="grid h-[500px] place-items-center">
          <Spinner size="lg" />
        </CardBody>
      </Card>
    );
  }
  const t = detail.data;
  if (!t) return null;
  const closed = t.status === 'CLOSED' || t.status === 'RESOLVED';

  return (
    <Card>
      <CardBody className="flex h-[calc(100vh-180px)] flex-col p-0">
        <div className="border-border border-b p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="text-ink-muted font-mono text-[11px] font-bold">
                {t.ticketNumber}
              </span>
              <h3 className="text-ink mt-1 text-base font-semibold">{t.subject}</h3>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge tone={STATUS_TONE[t.status] ?? 'neutral'}>{STATUS_LABEL[t.status]}</Badge>
                <Badge tone={PRIORITY_TONE[t.priority] ?? 'neutral'}>{t.priority}</Badge>
                <Badge tone="neutral">{t.category.replace(/_/g, ' ')}</Badge>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-ink-muted hover:text-ink h-8 w-8 rounded-lg"
            >
              <X className="mx-auto h-4 w-4" />
            </button>
          </div>
          {t.slaDueAt && !closed && (
            <p className="text-ink-muted mt-2 text-xs">
              <AlertTriangle className="mr-1 inline h-3 w-3" />
              SLA due {new Date(t.slaDueAt).toLocaleString()}
            </p>
          )}
        </div>

        <div className="bg-surface-subtle/10 flex-1 space-y-3 overflow-y-auto p-4">
          {t.messages.map((m) => {
            const fromMe = m.authorRole === 'VENDOR';
            const isInternal = m.internal;
            return (
              <div key={m.id} className={`flex flex-col ${fromMe ? 'items-end' : 'items-start'}`}>
                <div className="text-ink-muted mb-1 flex items-center gap-1.5 px-1 text-[11px] font-bold">
                  {isInternal && <Lock className="h-3 w-3 text-amber-500" />}
                  <span>{m.authorRole}</span>
                  <span className="text-ink-subtle text-[10px]">
                    {format(new Date(m.createdAt), 'h:mm a, MMM d')}
                  </span>
                </div>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${
                    isInternal
                      ? 'border border-amber-200 bg-amber-50 text-amber-900'
                      : fromMe
                        ? 'bg-ink rounded-tr-sm text-white'
                        : 'border-border bg-surface text-ink rounded-tl-sm border'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{m.body}</p>
                </div>
              </div>
            );
          })}

          {t.attachments.length > 0 && (
            <div className="border-border bg-surface mt-2 rounded-lg border p-3">
              <h4 className="text-ink-muted mb-2 text-xs font-bold uppercase">Attachments</h4>
              <ul className="space-y-1 text-sm">
                {t.attachments.map((a) => (
                  <li key={a.id}>
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand-primary inline-flex items-center gap-1"
                    >
                      <Paperclip className="h-4 w-4" />
                      {a.filename}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {!closed && (
          <div className="border-border bg-surface border-t p-3">
            {!showResolve ? (
              <>
                <div
                  className={`rounded-lg border ${
                    internal ? 'border-amber-300 bg-amber-50/30' : 'border-border bg-surface'
                  }`}
                >
                  <textarea
                    className="min-h-[80px] w-full resize-none bg-transparent px-3 py-2 text-sm outline-none"
                    placeholder={
                      internal
                        ? 'Internal note — only your team and admin see this'
                        : 'Reply to the customer…'
                    }
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                  />
                  <div className="border-border flex items-center justify-between border-t px-3 py-2">
                    <label className="flex cursor-pointer items-center gap-2 text-xs font-bold">
                      <input
                        type="checkbox"
                        checked={internal}
                        onChange={(e) => setInternal(e.target.checked)}
                      />
                      Internal note
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowResolve(true)}
                        className="border-border h-8 rounded-lg border px-3 text-xs font-bold"
                      >
                        Mark resolved
                      </button>
                      <button
                        type="button"
                        onClick={() => reply.trim() && send.mutate()}
                        disabled={!reply.trim() || send.isPending}
                        className="bg-brand-primary inline-flex h-8 items-center gap-1 rounded-lg px-3 text-xs font-bold text-white disabled:opacity-50"
                      >
                        <Send className="h-3.5 w-3.5" /> Send
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-xs font-bold text-emerald-700">
                  Resolution note (sent to the customer)
                </p>
                <textarea
                  className="border-border w-full rounded-lg border bg-white px-3 py-2 text-sm"
                  rows={3}
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  placeholder="Explain how the issue was resolved…"
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowResolve(false)}
                    className="border-border h-8 rounded-lg border bg-white px-3 text-xs font-bold"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={resolution.trim().length < 4 || resolve.isPending}
                    onClick={() => resolve.mutate()}
                    className="inline-flex h-8 items-center gap-1 rounded-lg bg-emerald-600 px-3 text-xs font-bold text-white disabled:opacity-50"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> Confirm resolve
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default CustomerSupportPage;
